import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ScrollArea } from '@/components/ui/scroll-area';
import { PullToRefresh } from '@/components/PullToRefresh';
import { Plane, Plus, CheckCircle2, AlertTriangle, XCircle, Edit2, FileText, BookOpen } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const statusConfig = {
  compliant: { label: 'COMPLIANT', color: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20', icon: CheckCircle2, dot: 'bg-emerald-500' },
  review_due: { label: 'REVIEW DUE', color: 'bg-amber-500/10 text-amber-400 border-amber-500/20', icon: AlertTriangle, dot: 'bg-amber-500' },
  non_compliant: { label: 'NON-COMPLIANT', color: 'bg-red-500/10 text-red-400 border-red-500/20', icon: XCircle, dot: 'bg-red-500' },
};

// Part categories with descriptions
const PART_CATEGORIES = [
  { id: 'ALL', label: 'ALL', description: 'All operations and MOS items' },
  { id: '135', label: 'Part 135', description: 'Small Aircraft Air Transport' },
  { id: '121', label: 'Part 121', description: 'Large Aircraft Air Transport' },
  { id: '138', label: 'Part 138', description: 'Aerial Work Operations' },
  { id: 'MOS 91', label: 'MOS 91', description: 'General Operating Rules' },
  { id: 'MOS 61', label: 'MOS 61', description: 'Flight Crew Licensing' },
  { id: 'MOS 121', label: 'MOS 121', description: 'Large Aircraft Standards' },
  { id: 'MOS 135', label: 'MOS 135', description: 'Small Aircraft Standards' },
  { id: 'MOS 138', label: 'MOS 138', description: 'Aerial Work Standards' },
  { id: 'MOS 139H', label: 'MOS 139H', description: 'Heliport Standards' },
];

// Available parts for the dropdown
const AVAILABLE_PARTS = [
  '135', '121', '138', 
  'MOS 91', 'MOS 61', 'MOS 121', 'MOS 135', 'MOS 138', 'MOS 139H'
];

export default function OperationsPage() {
  const [operations, setOperations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activePart, setActivePart] = useState('ALL');
  const [createOpen, setCreateOpen] = useState(false);
  const [editOp, setEditOp] = useState(null);
  const [form, setForm] = useState({ title: '', description: '', part: '135', status: 'compliant', notes: '' });

  const fetchOps = useCallback(async () => {
    try {
      const url = activePart !== 'ALL' ? `${API}/operations?part=${encodeURIComponent(activePart)}` : `${API}/operations`;
      const res = await fetch(url, { credentials: 'include' });
      if (res.ok) setOperations(await res.json());
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [activePart]);

  const handleRefresh = useCallback(async () => {
    await fetchOps();
    toast.success('Operations refreshed');
  }, [fetchOps]);

  useEffect(() => { fetchOps(); }, [fetchOps]);

  const handleCreate = async () => {
    try {
      const res = await fetch(`${API}/operations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(form)
      });
      if (res.ok) {
        toast.success('Operation created');
        setCreateOpen(false);
        setForm({ title: '', description: '', part: '135', status: 'compliant', notes: '' });
        fetchOps();
      }
    } catch { toast.error('Failed to create'); }
  };

  const handleUpdate = async () => {
    try {
      const res = await fetch(`${API}/operations/${editOp.operation_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(form)
      });
      if (res.ok) {
        toast.success('Operation updated');
        setEditOp(null);
        fetchOps();
      }
    } catch { toast.error('Update failed'); }
  };

  const openEdit = (op) => {
    setEditOp(op);
    setForm({ title: op.title, description: op.description, part: op.part, status: op.status, notes: op.notes || '' });
  };

  // Group operations by type (Parts vs MOS)
  const partOps = operations.filter(op => !op.part.startsWith('MOS'));
  const mosOps = operations.filter(op => op.part.startsWith('MOS'));

  const currentCategory = PART_CATEGORIES.find(p => p.id === activePart);
  const isMOSView = activePart.startsWith('MOS');

  return (
    <PullToRefresh onRefresh={handleRefresh} className="min-h-screen">
      <div data-testid="operations-page" className="p-4 sm:p-6 md:p-8 space-y-4 sm:space-y-6 animate-fade-in">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-100 tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>
              Operations & MOS Compliance
            </h1>
            <p className="text-slate-500 text-xs sm:text-sm mt-1">
              Part 135, 121, 138 & Manual of Standards tracking
            </p>
          </div>
          <Button
            data-testid="create-operation-btn"
            onClick={() => { setCreateOpen(true); setForm({ title: '', description: '', part: '135', status: 'compliant', notes: '' }); }}
            className="bg-[#003087] hover:bg-[#003087]/90 text-white w-full sm:w-auto"
          >
            <Plus className="h-4 w-4 mr-2" /> Add Operation
          </Button>
        </div>

        {/* Compliance Summary */}
        <div className="grid grid-cols-3 gap-2 sm:gap-4">
          <Card className="bg-slate-900 border-slate-800">
            <CardContent className="p-3 sm:p-4 flex items-center gap-2 sm:gap-3">
              <div className="h-8 w-8 sm:h-10 sm:w-10 rounded-md bg-emerald-500/10 flex items-center justify-center">
                <CheckCircle2 className="h-4 w-4 sm:h-5 sm:w-5 text-emerald-400" />
              </div>
              <div>
                <p className="text-lg sm:text-2xl font-bold text-slate-100">{operations.filter(o => o.status === 'compliant').length}</p>
                <p className="text-[10px] sm:text-xs text-slate-500">Compliant</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-slate-900 border-slate-800">
            <CardContent className="p-3 sm:p-4 flex items-center gap-2 sm:gap-3">
              <div className="h-8 w-8 sm:h-10 sm:w-10 rounded-md bg-amber-500/10 flex items-center justify-center">
                <AlertTriangle className="h-4 w-4 sm:h-5 sm:w-5 text-amber-400" />
              </div>
              <div>
                <p className="text-lg sm:text-2xl font-bold text-slate-100">{operations.filter(o => o.status === 'review_due').length}</p>
                <p className="text-[10px] sm:text-xs text-slate-500">Review Due</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-slate-900 border-slate-800">
            <CardContent className="p-3 sm:p-4 flex items-center gap-2 sm:gap-3">
              <div className="h-8 w-8 sm:h-10 sm:w-10 rounded-md bg-red-500/10 flex items-center justify-center">
                <XCircle className="h-4 w-4 sm:h-5 sm:w-5 text-red-400" />
              </div>
              <div>
                <p className="text-lg sm:text-2xl font-bold text-slate-100">{operations.filter(o => o.status === 'non_compliant').length}</p>
                <p className="text-[10px] sm:text-xs text-slate-500">Non-Compliant</p>
              </div>
            </CardContent>
          </Card>
        </div>

        <Tabs value={activePart} onValueChange={setActivePart}>
          <ScrollArea className="w-full">
            <TabsList className="bg-slate-900 border border-slate-800 p-1 inline-flex w-auto min-w-full">
              {PART_CATEGORIES.map(p => (
                <TabsTrigger
                  key={p.id}
                  value={p.id}
                  data-testid={`ops-tab-${p.id.toLowerCase().replace(' ', '-')}`}
                  className={`data-[state=active]:text-white text-slate-400 text-[10px] sm:text-xs font-mono whitespace-nowrap ${
                    p.id.startsWith('MOS') 
                      ? 'data-[state=active]:bg-purple-600' 
                      : 'data-[state=active]:bg-[#003087]'
                  }`}
                >
                  {p.label}
                </TabsTrigger>
              ))}
            </TabsList>
          </ScrollArea>

          <TabsContent value={activePart} className="mt-4">
            {currentCategory && currentCategory.id !== 'ALL' && (
              <div className="mb-4 p-3 bg-slate-900/50 border border-slate-800 rounded-lg flex items-center gap-3">
                {isMOSView ? (
                  <BookOpen className="h-5 w-5 text-purple-400" />
                ) : (
                  <Plane className="h-5 w-5 text-[#0ea5e9]" />
                )}
                <div>
                  <p className="text-sm font-medium text-slate-200">{currentCategory.label}</p>
                  <p className="text-xs text-slate-500">{currentCategory.description}</p>
                </div>
                <Badge className={`ml-auto ${isMOSView ? 'bg-purple-500/10 text-purple-400 border-purple-500/20' : 'bg-[#0ea5e9]/10 text-[#0ea5e9] border-[#0ea5e9]/20'} text-[10px]`}>
                  {operations.length} items
                </Badge>
              </div>
            )}

            {loading ? (
              <div className="space-y-3">
                {[...Array(4)].map((_, i) => (
                  <div key={i} className="h-[100px] bg-slate-900 border border-slate-800 rounded-lg animate-pulse" />
                ))}
              </div>
            ) : operations.length === 0 ? (
              <div className="text-center py-12 sm:py-16">
                <Plane className="h-10 w-10 sm:h-12 sm:w-12 text-slate-700 mx-auto mb-4" />
                <p className="text-slate-500">No operations tracked yet</p>
                <p className="text-xs text-slate-600 mt-1">Load sample data from the Dashboard or add an operation</p>
              </div>
            ) : (
              <div className="space-y-3">
                {operations.map((op) => {
                  const sc = statusConfig[op.status] || statusConfig.compliant;
                  const Icon = sc.icon;
                  const isMOS = op.part.startsWith('MOS');
                  return (
                    <Card key={op.operation_id} data-testid={`op-${op.operation_id}`} className="bg-slate-900 border-slate-800 shadow-sm hover:border-slate-700 transition-colors">
                      <CardContent className="p-3 sm:p-4 flex items-center gap-3 sm:gap-4">
                        <div className={`h-9 w-9 sm:h-10 sm:w-10 rounded-md flex items-center justify-center flex-shrink-0 ${sc.color}`}>
                          <Icon className="h-4 w-4 sm:h-5 sm:w-5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <h3 className="text-xs sm:text-sm font-medium text-slate-200 truncate">{op.title}</h3>
                            <Badge className={`text-[9px] sm:text-[10px] ${sc.color}`}>{sc.label}</Badge>
                          </div>
                          <p className="text-[10px] sm:text-xs text-slate-500 truncate">{op.description}</p>
                          <div className="flex gap-2 sm:gap-4 mt-2 text-[9px] sm:text-[10px] font-mono text-slate-500 flex-wrap">
                            <Badge className={`text-[9px] ${isMOS ? 'bg-purple-500/10 text-purple-400 border-purple-500/20' : 'bg-slate-800 text-slate-400 border-slate-700'}`}>
                              {op.part}
                            </Badge>
                            {op.next_review && <span className="hidden sm:inline">Next review: {new Date(op.next_review).toLocaleDateString()}</span>}
                          </div>
                        </div>
                        <Button
                          data-testid={`edit-op-${op.operation_id}`}
                          variant="ghost"
                          size="sm"
                          onClick={() => openEdit(op)}
                          className="text-slate-400 hover:text-white h-8 w-8 p-0"
                        >
                          <Edit2 className="h-4 w-4" />
                        </Button>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Create/Edit Dialog */}
        <Dialog open={createOpen || !!editOp} onOpenChange={() => { setCreateOpen(false); setEditOp(null); }}>
          <DialogContent className="max-w-lg w-[95vw] sm:w-full bg-[#0f172a] border-slate-700">
            <DialogHeader>
              <DialogTitle className="text-slate-200">{editOp ? 'Edit Operation' : 'New Operation'}</DialogTitle>
              <DialogDescription className="text-slate-500">Track compliance for RFDSSE operations and MOS requirements</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <Input data-testid="op-title-input" placeholder="Title" value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} className="bg-slate-900 border-slate-800 text-slate-200" />
              <Textarea data-testid="op-desc-input" placeholder="Description" value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} className="bg-slate-900 border-slate-800 text-slate-200" />
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-[10px] text-slate-500 uppercase tracking-wider mb-1 block">Part / MOS</label>
                  <Select value={form.part} onValueChange={(v) => setForm({...form, part: v})}>
                    <SelectTrigger data-testid="op-part-select" className="bg-slate-900 border-slate-800 text-slate-200"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#0f172a] border-slate-700 max-h-60">
                      <SelectItem value="135" className="text-slate-300 focus:bg-slate-800">Part 135</SelectItem>
                      <SelectItem value="121" className="text-slate-300 focus:bg-slate-800">Part 121</SelectItem>
                      <SelectItem value="138" className="text-slate-300 focus:bg-slate-800">Part 138</SelectItem>
                      <SelectItem value="MOS 91" className="text-purple-300 focus:bg-slate-800">MOS 91 - General Rules</SelectItem>
                      <SelectItem value="MOS 61" className="text-purple-300 focus:bg-slate-800">MOS 61 - Licensing</SelectItem>
                      <SelectItem value="MOS 121" className="text-purple-300 focus:bg-slate-800">MOS 121 - Large Aircraft</SelectItem>
                      <SelectItem value="MOS 135" className="text-purple-300 focus:bg-slate-800">MOS 135 - Small Aircraft</SelectItem>
                      <SelectItem value="MOS 138" className="text-purple-300 focus:bg-slate-800">MOS 138 - Aerial Work</SelectItem>
                      <SelectItem value="MOS 139H" className="text-purple-300 focus:bg-slate-800">MOS 139H - Heliports</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-[10px] text-slate-500 uppercase tracking-wider mb-1 block">Status</label>
                  <Select value={form.status} onValueChange={(v) => setForm({...form, status: v})}>
                    <SelectTrigger data-testid="op-status-select" className="bg-slate-900 border-slate-800 text-slate-200"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#0f172a] border-slate-700">
                      <SelectItem value="compliant" className="text-emerald-300 focus:bg-slate-800">Compliant</SelectItem>
                      <SelectItem value="review_due" className="text-amber-300 focus:bg-slate-800">Review Due</SelectItem>
                      <SelectItem value="non_compliant" className="text-red-300 focus:bg-slate-800">Non-Compliant</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <Textarea data-testid="op-notes-input" placeholder="Notes" value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} className="bg-slate-900 border-slate-800 text-slate-200" />
            </div>
            <DialogFooter className="mt-4">
              <Button variant="ghost" onClick={() => { setCreateOpen(false); setEditOp(null); }} className="text-slate-400">Cancel</Button>
              <Button data-testid="op-save-btn" onClick={editOp ? handleUpdate : handleCreate} className="bg-[#003087] hover:bg-[#003087]/90 text-white">
                {editOp ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </PullToRefresh>
  );
}
